
import React, { useEffect, useMemo, useState } from "react";
import { MenuOption, Table, TableColumn, TableMenu } from "../../components/Table";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import { FaChartBar, FaChartLine, FaChevronDown, FaChevronRight } from "react-icons/fa";
import FullPageSpinner from "../../components/FullPageSpinner";
import { sendRequest } from "../../utils/api";
import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ConfirmModalContext";
import { initAllStores } from "../../utils/cache";
import SalesSummaryModal from "../../Modal/accounting/SaleSummary";
import {
  computeBookingSummary, getExpenseByDate,
  groupOrderItemsByType, mergeSummaries
} from "../../utils/sales-summary";
import { syncBillHistory } from "../../utils/sync";
import BookedRoom from "../../models/BookedRoom";
import Order from "../../models/Order";
import { formatDate, formatDateForInput, getRangeForDate } from "../../utils/date";
import Expenditure from "../../models/Expenditure";
import { terminal } from "../../utils/user-helper";
import TaxModal from "../../Modal/accounting/TaxModal";

// ---------- Types ----------
type Sale = {
  idx: number;
  sn: number;
  createdAt: string;
  amount: number;
  status: string;
};

type AggregatedRow = {
  sn?: number;
  createdAt?: string;
  period: string;
  // total: number;
  status?: string;
};


// ---------- Grouping Functions ----------
function groupDaily(sales: Sale[]): AggregatedRow[] {
  return sales
    // .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map((s,) => ({
      createdAt: s.createdAt,
      period: formatDate(s.createdAt),
      total: s.amount,
      sn: s.sn,
      amount: s.amount.toLocaleString(),
      status: s.status.toLocaleLowerCase() === "approved"
        ? "Approved" : "Pending", // ✅ direct mapping
    })).sort((a, b) => a.sn - b.sn);
}

function groupWeekly(sales: Sale[]): AggregatedRow[] {
  const map = new Map<string, Sale[]>();

  sales.forEach((s) => {
    const d = new Date(s.createdAt);
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - d.getDay()); // Sunday start
    const key = weekStart.toISOString().split("T")[0];

    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(s);
  });

  return Array.from(map.entries())
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .map(([weekStart, items]) => {
      const approvedDays = items.filter(i => i.status).length;
      const totalDays = items.length; // days that have sales

      return {
        period: weekStart,
        total: items.reduce((sum, i) => sum + i.amount, 0),
        status: `${approvedDays}/${totalDays}`, // ✅ e.g. 1/7
      };
    });
}


function groupMonthly(sales: Sale[]): AggregatedRow[] {
  const map = new Map<string, Sale[]>();

  sales.forEach((s) => {
    const d = new Date(s.createdAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(s);
  });

  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, items]) => {
      const approvedDays = items.filter(i => i.status).length;
      const totalDays = items.length;

      return {
        period: month,
        total: items.reduce((sum, i) => sum + i.amount, 0),
        status: `${approvedDays}/${totalDays}`, // ✅ e.g. 15/31
      };
    });
}


// ---------- Collapsible Section ----------
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center px-4 py-3 border-b text-left font-medium"
      >
        <span>{title}</span>
        <span className="text-gray-600">{open
          ? <span>{FaChevronDown({})}</span>
          : <span>{FaChevronRight({})}</span>}</span>
      </button>
      <div className={`transition-all duration-300 overflow-hidden ${open ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}


// ---------- MAIN COMPONENT ----------
export default function Sales() {

  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();

  const [terminalId] = useState(() => terminal());

  const [sales, setSales] = useState<Sale[]>([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartType, setChartType] = useState<"line" | "bar">("line");
  const [period, setPeriod] = useState<"daily" | "weekly" | "monthly">("daily");

  const [accountSummary, setAccountSummary] = useState({} as any);
  const [salesDate, setSalesDate] = useState("");

  const [filtered, setFiltered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [showTaxModal, setShowTaxModal] = useState(false);

  const fetchSalesData = async (startDate: string, endDate: string) => {
    setLoading(true);

    const terminalId = terminal();
    
    try {
      // Fetch sales from API
      const url = `/sales?terminal=${terminalId}&` +
        `startDate=${startDate}&endDate=${endDate}`
      const response = await sendRequest("GET", url);

      const sales: Sale[] = response.sales || [];

      const { start, end } = response.date || {};

      if (start && end) {
        setStartDate(formatDateForInput(start));
        setEndDate(formatDateForInput(end));
      }

      setSales(sales);

    } catch (err: any) {
      console.log(err)
      showToast("Failed to load sales data", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const approveSale = async (sale: AggregatedRow) => {
    openModal(
      "Confirm Sales Approval",
      `Are you sure you want to approve sales for ${sale.period}?`,
      async () => {
        try {

          // const formattedDate = sale.period.split("-").join("");
          const formattedDate = formatDateForInput(sale.createdAt ?? "");
          setLoading(true);
          await sendRequest("PUT", "/sales/approve", {
            date: formattedDate, terminal: terminalId
          });

          setSales(prev =>
            prev.map(s =>
              s.sn === sale.sn
                ? { ...s, status: "Approved" }
                : s
            )
          );

          // 🔁 Re-fetch to keep UI in sync with backend
          // await fetchSalesData();

          showToast("Sales approved successfully.", { type: "success" });
        } catch (error) {
          console.error(error);
          showToast(
            "An error occurred while approving the sales. Please try again.",
            { type: "error" }
          );
        } finally {
          setLoading(false);
        }
      },
      true,
      "Yes, Approve",
      "Cancel"
    );
  };

  const salesDetails = async (sale: AggregatedRow) => {

    // if (period !== "daily") {
    //   return openModal(
    //     "Coming Soon 🚀",
    //     "Weekly and monthly sales reports are currently under development and will be available soon.",
    //     () => {
    //       closeModal();
    //     },
    //     false,
    //     "Got it"
    //   );
    // }

    setLoading(true);

    try {

      const cache = await initAllStores();

      const period = sale.period;
      const date = { start: period, end: period };
      await syncBillHistory({ bookedRooms: [], orders: [] }, date);

      const { start, end } = getRangeForDate(period);

      const todayBookings: BookedRoom[] = await cache.getAllBy(
        "bookedRooms", { terminal: terminalId },
        "createdAt", "asc", { field: "createdAt", start, end }
      );

      const todayOrders: Order[] = await cache.getAllBy(
        "orders", { terminal: terminalId }, "createdAt", "asc", { field: "createdAt", start, end }
      );

      const { foods, drinks, games, laundries } = await groupOrderItemsByType(todayOrders);
      const bookingSummary = computeBookingSummary(todayBookings);
      const totalSummary = mergeSummaries({
        foods: foods.summary,
        drinks: drinks.summary,
        laundries: laundries.summary,
        games: games.summary,
        rooms: bookingSummary
      });

      const { expenditures } = await getExpenseByDate(terminalId, { start: period, end: period });
      const totalExpenditure = (expenditures as Expenditure[] ?? []).reduce(
        (sum, e) => sum + e.amount,
        0 // initial value
      );

      totalSummary.expenses = totalExpenditure;
      setSalesDate(formatDate(period));
      setAccountSummary(totalSummary);
      setShowSalesModal(true);
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSalesData(startDate, endDate);
  }, []);

  // Apply filter only when button clicked
  // const filteredSales = useMemo(() => {
  //   if (!filtered) return sales;
  //   if (startDate && endDate && filtered) {

  //       const start = startDate;



  //     console.log({ });
  //     // return sales.filter((s) => {

  //     //   const end = endDate ? new Date(endDate).getTime() : Infinity;
  //     //   return time >= start && time <= end;
  //     // });
  //   }
  // }, [sales, startDate, endDate, filtered]);




  // Group data
  const dailyRows = useMemo(() => groupDaily(sales), [sales]);
  const weeklyRows = useMemo(() => groupWeekly(sales), [sales]);
  const monthlyRows = useMemo(() => groupMonthly(sales), [sales]);
  const activeRows = period === "daily" ? dailyRows : period === "weekly" ? weeklyRows : monthlyRows;

  // Total sales only when filter applied
  const totalSales = sales.reduce((a, b) => a + b.amount, 0);

  // Table columns
  const columns: TableColumn[] = [
    { key: "sn", label: "S/N", responsive: "md" },
    {
      key: "period",
      label: period === "daily"
        ? "Date" : period === "weekly"
          ? "Week Start" : "Month",
    },
    { key: "amount", label: "Amount" },
    {
      key: "status",
      label: "Status",
      getClassName: (value: string) => {
        // Daily
        if (value.toLocaleLowerCase() === "approved") {
          return "text-green-600 font-semibold";
        }

        if (value.toLocaleLowerCase() === "pending") {
          return "text-red-500 font-semibold";
        }

        // Weekly / Monthly → fraction x/y
        if (value.includes("/")) {
          const [approved, total] = value.split("/").map(Number);

          return approved === total
            ? "text-green-600 font-semibold"
            : "text-red-500 font-semibold";
        }

        return "";
      },
    }

  ];

  return (
    <div className="flex-1 p-4 md:p-6 space-y-6 overflow-y-auto ">
      <FullPageSpinner show={loading} />

      <SalesSummaryModal
        show={showSalesModal}
        onClose={() => setShowSalesModal(false)}
        date={salesDate}
        accountSummary={accountSummary}
      />
      <TaxModal
        show={showTaxModal}
        onClose={() => setShowTaxModal(false)}
      // terminalId={terminalId}
      />
      {/* <h1 className="text-2xl font-bold mb-6">Sales</h1> */}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Sales</h1>

        <button
          className="px-4 py-1 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
          onClick={() => setShowTaxModal(true)}
        >
          Calculate Tax
        </button>
      </div>
      {/* ---------- PERIOD TOGGLE ---------- */}
      <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mb-6">
        {/* Left: Period Buttons */}
        {/* <div className="flex flex-wrap gap-3">
          {(["daily", "weekly", "monthly"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 rounded border font-medium ${period === p ? "bg-blue-600 text-white" : "bg-gray-200"
                }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div> */}

        {/* Right: Calculate Tax */}
        {/* <div className="flex sm:items-center">
          <button
            className="px-4 py-1 bg-green-600 text-white rounded-md font-medium hover:bg-green-700"
            onClick={() => setShowTaxModal(true)}
          >
            Calculate Tax
          </button>
        </div> */}
      </div>

      {/* ---------- FILTERS & TOTAL SALES ---------- */}
      <Section title="Filters & Total Sales">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">

          {/* Left side: Dates */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">

            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1">Start Date</label>
              <input
                type="date"
                className="border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                value={startDate}
                onChange={(e) => {
                  const newStart = e.target.value;

                  if (endDate && newStart > endDate) {
                    showToast("⚠️ Start date cannot be later than end date.", { type: "warning" });
                    return;
                  }
                  setStartDate(newStart)
                }}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1">End Date</label>
              <input
                type="date"
                className="border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                value={endDate}
                onChange={(e) => {
                  const newEnd = e.target.value;

                  if (!startDate) {
                    showToast("Please select a start date first.", { type: "info" });
                    return;
                  }

                  if (newEnd < startDate) {
                    showToast("End date cannot be earlier than start date.", { type: "warning" });
                    return;
                  }

                  setEndDate(newEnd);
                }}
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setFiltered(true);
                  fetchSalesData(startDate, endDate);
                }}
                disabled={!startDate || !endDate}
                className={`px-4 py-2 rounded-md shadow font-medium ${!startDate || !endDate
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
              >
                Apply
              </button>

              <button
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  fetchSalesData("", "");
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md shadow hover:bg-gray-300"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Right side: Total Sales */}
          {totalSales > 0 && (
            <div className="bg-white border border-blue-300 p-3 rounded-md shadow">
              <span className="text-xl font-bold text-blue-800">
                ₦{totalSales.toLocaleString()}
              </span>
            </div>
          )}

        </div>
      </Section>

      {/* ---------- CHART ---------- */}
      <Section title="Sales Trend Chart">
        <div className="flex gap-3 mb-4">
          <button
            onClick={() => setChartType("line")}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border font-medium ${chartType === "line" ? "bg-blue-600 text-white" : "bg-gray-100"
              }`}
          >
            {FaChartLine({})}
            Line
          </button>
          <button
            onClick={() => setChartType("bar")}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border font-medium ${chartType === "bar" ? "bg-blue-600 text-white" : "bg-gray-100"
              }`}
          >
            {FaChartBar({})}
            Bar
          </button>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          {chartType === "line" ? (
            <LineChart data={activeRows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis tickFormatter={(v) => "₦" + v.toLocaleString()} />
              <Tooltip formatter={(val: number | undefined) => "₦" + val?.toLocaleString()} labelFormatter={(label) => `Period: ${label}`} />
              <Line type="monotone" dataKey="total" stroke="#2563eb" strokeWidth={3} dot={false} />
            </LineChart>
          ) : (
            <BarChart data={activeRows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis tickFormatter={(v) => "₦" + v.toLocaleString()} />
              <Tooltip formatter={(val: number | undefined) => "₦" + val?.toLocaleString()} labelFormatter={(label) => `Period: ${label}`} />
              <Bar dataKey="total" fill="#2563eb" />
            </BarChart>
          )}
        </ResponsiveContainer>
      </Section>

      {/* ---------- TABLE ---------- */}
      <Section title="Sales Table">
        <Table
          columns={columns}
          data={activeRows}
          rowsPerPage={5}
          tableMenu={(row: any, closeMenu) => (
            <TableMenu
              row={row}
              options={[

                {
                  label: "View Details",
                  onClick: (s: AggregatedRow) => {
                    salesDetails(s);
                  },
                },
                period === "daily"
                  ? {
                    label: "Approve Sale",
                    onClick: (s: AggregatedRow) => {
                      approveSale(s)
                    },
                  }
                  : null,
              ].filter(Boolean) as MenuOption[]}
              closeMenu={closeMenu}
            />
          )}
        />
      </Section>
    </div>
  );
}